import React from 'react';
import { useTranslation } from 'react-i18next';

interface InvestorInfo {
  id: string;
  code: string;
  fullname: string;
  email: string;
  phone: string;
  avatar_url?: string;
  gender: number | null;
  birthday: string | null;
  job_title: string | null;
  identifier: string;
  status: 'Active' | 'Inactive';
}

interface InvestorProfileCardProps {
  investor: InvestorInfo;
}

const InvestorProfileCard: React.FC<InvestorProfileCardProps> = ({ investor }) => {
  const { t } = useTranslation();

  const getGenderText = (gender: number | null) => {
    if (gender === 1) return t('profile.male', 'Nam');
    if (gender === 2) return t('profile.female', 'Nữ');
    return t('profile.other', 'Khác');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm overflow-hidden">
      {/* Header gradient */}
      <div className="h-24 bg-gradient-to-r from-blue-100 via-pink-100 to-purple-100 dark:from-blue-900/30 dark:via-pink-900/30 dark:to-purple-900/30" />

      {/* Avatar */}
      <div className="flex justify-center -mt-12">
        {investor.avatar_url ? (
          <img
            src={investor.avatar_url}
            alt={investor.fullname}
            className="w-24 h-24 rounded-full border-4 border-white dark:border-gray-800 object-cover"
          />
        ) : (
          <div className="w-24 h-24 rounded-full border-4 border-white dark:border-gray-800 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <svg
              className="w-12 h-12 text-gray-400 dark:text-gray-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
        )}
      </div>

      {/* Profile Info */}
      <div className="p-6 pt-4">
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {t('investorDetail.personalInfo', 'Thông tin cá nhân')}
          </h3>

          <div className="space-y-3">
            <div className="flex">
              <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.fullName', 'Họ và Tên')}
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                : {investor.fullname || '-'}
              </span>
            </div>

            <div className="flex">
              <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.email', 'Email')}
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                : {investor.email || '-'}
              </span>
            </div>

            <div className="flex">
              <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.phone', 'Số điện thoại')}
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                : {investor.phone || '-'}
              </span>
            </div>

            <div className="flex">
              <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.gender', 'Giới tính')}
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                : {getGenderText(investor.gender)}
              </span>
            </div>

            <div className="flex">
              <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.birthday', 'Sinh nhật')}
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                : {investor.birthday || '-'}
              </span>
            </div>

            <div className="flex">
              <span className="w-28 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.occupation', 'Nghề nghiệp')}
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                : {investor.job_title || '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorProfileCard;
